import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FileText, Download, RefreshCw } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";
import { Button } from "@core/components/ui/button";
import { billingService } from "@core/services/illing";
import { BILLING_CACHE_CONFIG } from "@core/constants/billing";
import type { Invoice } from "@core/services/illing/types";
import AccountPageTemplate from "./AccountPageTemplate";

const INVOICE_STATUS_CONFIG = {
  paid: {
    labelKey: "account.invoices.status.paid",
    className: "bg-success/15 text-success border border-success/30",
  },
  pending: {
    labelKey: "account.invoices.status.pending",
    className: "bg-warning/15 text-warning border border-warning/30",
  },
  refunded: {
    labelKey: "account.invoices.status.refunded",
    className: "bg-muted text-muted-foreground border border-border",
  },
} as const;

function formatAmount(amount: number, currency: string): string {
  // LemonSqueezy retourne les montants en cents
  const value = amount / 100;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: currency || "EUR",
  }).format(value);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function InvoiceStatusBadge({ status }: { status: Invoice["status"] }) {
  const { t } = useTranslation("common");
  const config = INVOICE_STATUS_CONFIG[status] ?? INVOICE_STATUS_CONFIG.pending;
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium ${config.className}`}
    >
      {t(config.labelKey)}
    </span>
  );
}

function InvoiceRowSkeleton() {
  return (
    <div className="flex items-center justify-between p-4 border-b border-border/50 last:border-0 animate-pulse">
      <div className="space-y-2">
        <div className="h-4 w-24 bg-muted rounded" />
        <div className="h-3 w-32 bg-muted/60 rounded" />
      </div>
      <div className="flex items-center gap-4">
        <div className="h-4 w-16 bg-muted rounded" />
        <div className="h-5 w-16 bg-muted/60 rounded-full" />
        <div className="h-8 w-28 bg-muted/40 rounded" />
      </div>
    </div>
  );
}

export default function AccountInvoices() {
  const { t } = useTranslation("common");
  const {
    data: invoices,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["invoices"],
    queryFn: () => billingService.getInvoices(),
    staleTime: BILLING_CACHE_CONFIG.STALE_TIME,
  });

  return (
    <AccountPageTemplate
      title={t("account.invoices.title")}
      description={t("account.invoices.description")}
      testId="account-invoices-page"
    >
      <Card data-testid="invoices-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            {t("account.invoices.history_title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div data-testid="invoices-loading">
              <InvoiceRowSkeleton />
              <InvoiceRowSkeleton />
              <InvoiceRowSkeleton />
            </div>
          ) : error ? (
            <div
              className="flex flex-col items-center justify-center py-12 px-6 text-center"
              data-testid="invoices-error"
            >
              <p className="text-destructive mb-3">
                {t("account.invoices.error")}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {t("account.invoices.retry")}
              </Button>
            </div>
          ) : !invoices || invoices.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-12 px-6 text-center"
              data-testid="invoices-empty"
            >
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground font-medium">
                {t("account.invoices.empty_title")}
              </p>
              <p className="text-muted-foreground text-sm mt-1">
                {t("account.invoices.empty_description")}
              </p>
            </div>
          ) : (
            <div data-testid="invoices-list">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-4 border-b border-border/50 last:border-0 hover:bg-accent transition-colors"
                  data-testid={`invoice-row-${invoice.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground text-sm">
                      {t("account.invoices.order_number", {
                        number: invoice.number,
                      })}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatDate(invoice.createdAt.toISOString())}
                    </p>
                  </div>

                  <div className="flex items-center gap-4 shrink-0 ml-4">
                    <span className="font-semibold text-foreground text-sm">
                      {formatAmount(invoice.amount, invoice.currency)}
                    </span>
                    <InvoiceStatusBadge status={invoice.status} />
                    {invoice.pdfUrl ? (
                      <a
                        href={invoice.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`invoice-download-${invoice.id}`}
                      >
                        <Button variant="outline" size="sm" className="gap-2">
                          <Download className="w-3.5 h-3.5" />
                          {t("account.invoices.download")}
                        </Button>
                      </a>
                    ) : (
                      <Button variant="ghost" size="sm" disabled>
                        {t("account.invoices.no_receipt")}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </AccountPageTemplate>
  );
}
